import { createApp } from "vue";
import { createPinia } from "pinia";
import App from "./App.vue";
import { createShellRouter } from "./router";
import { maybeRedirectLegacyCloudEditorUrl } from "./lib/cloudEditorHost";
import { initUiZoom } from "./composables/useUiZoom";
import "./styles.css";
import * as Sentry from "@sentry/vue";
import { isTryHost } from "./lib/hostGate";
import { shouldDropSentryEvent } from "./lib/sentryBenignErrors";
import { handleSentryFeedbackFormClose } from "./lib/sentryFeedback";

initUiZoom();

// Legacy `/editor/manuscript?project=cloud://…` → `/editor/<uuid>/#/…`
if (maybeRedirectLegacyCloudEditorUrl()) {
  // Full navigation in progress — don't mount the SPA on the old URL.
} else {
  const router = createShellRouter();
  const rawHash =
    typeof window !== "undefined" ? window.location.hash.replace(/^#/, "") : "";
  const pathOnly = rawHash.split("?")[0] ?? "/";
  // Interim boot until App.vue resolves WindowKind — avoids flashing the editor
  // chrome on the splash window. Preserve intentional deep hashes.
  if (pathOnly === "/" || pathOnly === "") {
    // Keep the query (e.g. `?demo=1&legacy=1`) so App.vue can still read it.
    const query = rawHash.includes("?")
      ? rawHash.slice(rawHash.indexOf("?"))
      : "";
    void router.replace(`/boot${query}`);
  }
  const app = createApp(App);

  const tryHost = isTryHost();
  if (import.meta.env.PROD) {
    Sentry.init({
      app,
      dsn: "https://c805f0e13f4178db24b14506e55ae2c7@o288223.ingest.us.sentry.io/4512040568160256",
      environment: tryHost ? "try" : "production",
      ignoreErrors: [/^AbortError$/i, /The operation was aborted/i],
      beforeSend(event, hint) {
        return shouldDropSentryEvent(event, hint) ? null : event;
      },
      dataCollection: {
        // To disable sending user data and HTTP bodies, uncomment the lines below. For more info visit:
        // https://docs.sentry.io/platforms/javascript/guides/vue/configuration/options/#dataCollection
        userInfo: false,
        httpBodies: [],
      },
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration(),
        // No feedback on try.*; elsewhere we open via the status-bar control and
        // restyle the widget to match the dock panel (see sentryFeedback.ts).
        ...(tryHost
          ? []
          : [
              Sentry.feedbackIntegration({
                autoInject: false,
                showBranding: false,
                enableScreenshot: true,
                colorScheme: document.documentElement.classList.contains("dark")
                  ? "dark"
                  : "light",
                formTitle: "Send feedback",
                submitButtonLabel: "Send feedback",
                messageLabel: "What happened?",
                messagePlaceholder: "What went wrong, or what did you expect?",
                nameLabel: "Name (optional)",
                emailLabel: "Email (optional)",
                addScreenshotButtonLabel: "Add screenshot",
                removeScreenshotButtonLabel: "Remove screenshot",
                successMessageText: "Thanks for the feedback!",
                themeLight: {
                  background: "var(--bg-raised)",
                  foreground: "var(--text)",
                  accentBackground: "var(--color-zw-amber)",
                  accentForeground: "var(--bg-raised)",
                  border: "1px solid var(--line)",
                  boxShadow: "none",
                  outline: "2px solid var(--color-zw-amber)",
                },
                themeDark: {
                  background: "var(--bg-raised)",
                  foreground: "var(--text)",
                  accentBackground: "var(--color-zw-amber)",
                  accentForeground: "var(--bg-raised)",
                  border: "1px solid var(--line)",
                  boxShadow: "none",
                  outline: "2px solid var(--color-zw-amber)",
                },
                onFormClose: () => handleSentryFeedbackFormClose(),
                onFormSubmitted: () => handleSentryFeedbackFormClose(),
              }),
            ]),
      ],
      tracesSampleRate: 1.0,
      // Propagate traces to local Phoenix + production cloud API (VITE_CLOUD_API_URL)
      tracePropagationTargets: [
        "localhost",
        /^\//,
        /^https:\/\/app\.zephyrwrite\.com/,
      ],
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    });
  }

  app.use(createPinia()).use(router).mount("#app");
}
